/**
 * SitePick front — 24 時間取置きの申込み / 再販通知の登録 / holder 本人の表示（reservation-notify §9）。
 *
 * - `.js-sp-hold-toggle`（parts/hold-link.php）/ `.js-sp-restock`（parts/restock-form.php）でフォームを展開する。
 * - 送信は `POST /holds` / `POST /restock`（JSON）。フロントが送るのは商品 ID・メールアドレス・
 *   FormGuard の値（hp_url / t / tsig）・Turnstile のトークン・ctx だけ。可否はすべてサーバが判定する。
 * - 二重送信は contact.js と同じ型（pending フラグ + submit の disabled）で抑止する。
 * - GA4: 2xx の後に 1 回だけ `request_hold` / `subscribe_restock`（item_id + hold_context）。メールは載せない。
 * - holder 本人の表示: `GET /holds/viewer?product_id=`（no-store。HttpOnly の `sp_hold` Cookie をサーバが読む）の
 *   結果が holder なら、他ユーザー向けの要素（`data-sp-hold-other`）を隠し、`data-sp-hold-viewer` に
 *   「あなたが取置き中です（…まで）」+「取置きページを開く」+ `.js-sp-checkout`（申込みボタン）を描く。
 *   ページ HTML は Cookie で分岐しない（将来のページキャッシュに備える）。
 */

import { announce } from "./live-region.js";
import { push, readItem } from "./analytics.js";

const TIMEOUT_MS = 15000;

/** @type {WeakSet<HTMLFormElement>} */
const pendingForms = new WeakSet();

function restBase() {
  const cfg = window.SITEPICK || {};
  const base = typeof cfg.restUrl === "string" && cfg.restUrl ? cfg.restUrl : "/wp-json/sitepick/v1/";
  return base.endsWith("/") ? base : `${base}/`;
}

function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

/**
 * タイムアウト付きの fetch（AbortController）。
 *
 * @param {string} url
 * @param {RequestInit} init
 * @returns {Promise<Response>}
 */
async function fetchWithTimeout(url, init) {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? window.setTimeout(() => controller.abort(), TIMEOUT_MS) : 0;
  try {
    return await fetch(url, Object.assign({}, init, controller ? { signal: controller.signal } : {}));
  } finally {
    if (timer) {
      window.clearTimeout(timer);
    }
  }
}

/* ---------------------------------------------------------------- Turnstile */

/**
 * フォームを開いたときに Turnstile を描く（explicit render。スクリプトは defer なので読み込みを待つ）。
 *
 * @param {HTMLFormElement} form
 * @param {number} [attempt]
 * @returns {void}
 */
function renderTurnstile(form, attempt = 0) {
  const box = form.querySelector(".js-sp-turnstile");
  if (!(box instanceof HTMLElement) || box.dataset.spWidgetId) {
    return;
  }
  if (attempt === 0 && box.dataset.spRendering === "1") {
    // 既に描画待ち（スクリプトの読み込み待ちでリトライ中）。
    return;
  }
  box.dataset.spRendering = "1";
  const ts = window.turnstile;
  if (ts && typeof ts.render === "function") {
    try {
      // 狭い画面（SP の CTA 内）では通常サイズ（300px）がはみ出すので compact にする。
      const options = { sitekey: box.dataset.sitekey || "", size: box.clientWidth > 0 && box.clientWidth < 300 ? "compact" : "normal" };
      if (box.dataset.action) {
        options.action = box.dataset.action;
      }
      const id = ts.render(box, options);
      if (id !== undefined && id !== null) {
        box.dataset.spWidgetId = String(id);
      }
    } catch {
      /* 描画できない（キー不正など）→ サーバが captcha_failed を返し、文言で案内する */
    }
    box.dataset.spRendering = "";
    return;
  }
  if (attempt < 40) {
    window.setTimeout(() => renderTurnstile(form, attempt + 1), 250);
  } else {
    box.dataset.spRendering = "";
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function turnstileToken(form) {
  const field = form.elements.namedItem("cf-turnstile-response");
  if (field && "value" in field && field.value) {
    return String(field.value);
  }
  const box = form.querySelector(".js-sp-turnstile");
  const ts = window.turnstile;
  if (box instanceof HTMLElement && box.dataset.spWidgetId && ts && typeof ts.getResponse === "function") {
    try {
      return String(ts.getResponse(box.dataset.spWidgetId) || "");
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * トークンは 1 回使い切りなので、失敗のたびにリセットする。
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function resetTurnstile(form) {
  const box = form.querySelector(".js-sp-turnstile");
  const ts = window.turnstile;
  if (box instanceof HTMLElement && box.dataset.spWidgetId && ts && typeof ts.reset === "function") {
    try {
      ts.reset(box.dataset.spWidgetId);
    } catch {
      /* noop */
    }
  }
}

/* ---------------------------------------------------------------- 展開 */

/**
 * @param {HTMLElement} root `[data-sp-hold]` / `[data-sp-restock]`
 * @param {boolean} open
 * @returns {void}
 */
function setOpen(root, open) {
  const toggle = root.querySelector(".js-sp-hold-toggle, .js-sp-restock");
  const form = root.querySelector("form");
  if (!(form instanceof HTMLFormElement)) {
    return;
  }
  form.hidden = !open;
  root.classList.toggle("is-open", open);
  if (toggle) {
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
  }
  if (open) {
    renderTurnstile(form);
    const email = form.querySelector("input[name=\"email\"]");
    if (email instanceof HTMLInputElement && !email.disabled) {
      email.focus();
    }
  }
}

/**
 * @param {MouseEvent} event
 * @returns {void}
 */
function onToggleClick(event) {
  const toggle = event.target instanceof Element ? event.target.closest(".js-sp-hold-toggle, .js-sp-restock") : null;
  if (!toggle) {
    return;
  }
  const root = toggle.closest("[data-sp-hold], [data-sp-restock]");
  if (!(root instanceof HTMLElement)) {
    return;
  }
  event.preventDefault();
  setOpen(root, toggle.getAttribute("aria-expanded") !== "true");
}

/* ---------------------------------------------------------------- 送信 */

/**
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function clearErrors(form) {
  form.querySelectorAll("[data-sp-error]").forEach((box) => {
    box.textContent = "";
    box.hidden = true;
  });
  form.querySelectorAll("[aria-invalid=\"true\"]").forEach((field) => field.removeAttribute("aria-invalid"));
  form.classList.remove("has-error");
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} text
 * @returns {void}
 */
function showFieldError(form, name, text) {
  const box = form.querySelector(`[data-sp-error="${name}"]`);
  if (box instanceof HTMLElement) {
    box.textContent = text;
    box.hidden = false;
  }
  form.classList.add("has-error");
  if (name === "email") {
    const field = form.querySelector("input[name=\"email\"]");
    if (field instanceof HTMLInputElement) {
      field.setAttribute("aria-invalid", "true");
      field.focus();
    }
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} text
 * @returns {void}
 */
function setStatus(form, text) {
  const status = form.querySelector("[data-sp-form-status]");
  if (status instanceof HTMLElement) {
    status.textContent = text;
    status.hidden = text === "";
  }
}

/**
 * 受付後はフォームの入力部分を閉じて、案内文だけを残す（同じ申込みを重ねて送らせない）。
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function finishForm(form) {
  form.classList.add("is-done");
  form.querySelectorAll(".p-hold-form__lead, .p-hold-form__row, .p-hold-form__consent, .p-hold-form__turnstile, .p-hold-form__actions").forEach((node) => {
    if (node instanceof HTMLElement) {
      node.hidden = true;
    }
  });
}

/**
 * 失敗時の文言（REST の status / code から）。
 *
 * @param {"holds"|"restock"} endpoint
 * @param {number} status
 * @param {Object} data
 * @returns {string}
 */
export function failureMessage(endpoint, status, data) {
  const code = data && typeof data.code === "string" ? data.code : "";
  const isHold = endpoint === "holds";
  if (status === 409) {
    if (isHold) {
      return code === "held_by_other"
        ? msg("holdHeldByOther", "現在、取置き中です")
        : msg("holdRefused", "このサイトは現在取置きを受け付けていません（購入は可能です）");
    }
    return msg("restockRefused", "この商品は現在、通知の登録を受け付けていません。ページを再読み込みしてください。");
  }
  if (status === 403 && code === "captcha_failed") {
    return msg("holdCaptcha", "送信時の確認に失敗しました。少し時間をおいて、もう一度お試しください。");
  }
  if (status === 403) {
    return msg("holdForbidden", "受け付けられませんでした。ページを再読み込みして、もう一度お試しください。");
  }
  if (status === 422) {
    return msg("holdInvalidEmail", "メールアドレスをご確認ください。");
  }
  if (status === 429) {
    return msg("rateLimited", "アクセスが集中しています。しばらくして再度お試しください。");
  }
  if (status === 503) {
    return isHold
      ? msg("holdUnavailable", "現在、取置きを受け付けていません。時間をおいてお試しください（購入は可能です）")
      : msg("restockUnavailable", "現在、通知の登録を受け付けていません。時間をおいてお試しください。");
  }
  return msg("holdError", "送信できませんでした。時間をおいて再度お試しください。");
}

/**
 * GA4 の item_id（SKU）。フォームの root に埋めた値 → ページの data-sp-item の順。
 *
 * @param {HTMLElement} root
 * @returns {string}
 */
function itemIdFor(root) {
  const direct = root.getAttribute("data-sp-item-id");
  if (direct) {
    return direct;
  }
  const item = readItem(root);
  return item && typeof item.item_id === "string" ? item.item_id : "";
}

/**
 * @param {SubmitEvent} event
 * @returns {Promise<void>}
 */
async function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(".js-sp-hold-form, .js-sp-restock-form")) {
    return;
  }
  event.preventDefault();
  if (pendingForms.has(form)) {
    return;
  }
  const root = form.closest("[data-sp-hold], [data-sp-restock]");
  if (!(root instanceof HTMLElement)) {
    return;
  }
  const endpoint = form.getAttribute("data-sp-endpoint") === "restock" ? "restock" : "holds";
  const productId = parseInt(root.getAttribute("data-sp-id") || "0", 10);
  if (!Number.isInteger(productId) || productId <= 0) {
    return;
  }
  const ctx = root.getAttribute("data-sp-ctx") || "detail";
  const emailField = form.querySelector("input[name=\"email\"]");
  const email = emailField instanceof HTMLInputElement ? emailField.value.trim() : "";

  clearErrors(form);
  if (email === "" || (emailField instanceof HTMLInputElement && !emailField.checkValidity())) {
    const text = msg("holdInvalidEmail", "メールアドレスをご確認ください。");
    showFieldError(form, "email", text);
    announce(text);
    return;
  }

  const field = (name) => {
    const el = form.elements.namedItem(name);
    return el && "value" in el ? String(el.value) : "";
  };
  const payload = {
    product_id: productId,
    email,
    hp_url: field("hp_url"),
    t: parseInt(field("t") || "0", 10) || 0,
    tsig: field("tsig"),
    ctx,
  };
  if (endpoint === "restock") {
    payload.consent = 1;
  }
  const token = turnstileToken(form);
  if (token) {
    payload.turnstile_token = token;
  }

  const cfg = window.SITEPICK || {};
  const submit = form.querySelector("button[type=\"submit\"]");
  pendingForms.add(form);
  form.classList.add("is-loading");
  if (submit instanceof HTMLButtonElement) {
    submit.disabled = true;
    submit.setAttribute("aria-busy", "true");
  }
  setStatus(form, msg("holdSending", "送信しています…"));

  try {
    const res = await fetchWithTimeout(`${restBase()}${endpoint}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": typeof cfg.nonce === "string" ? cfg.nonce : "",
      },
      body: JSON.stringify(payload),
    });
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (res.ok) {
      const done = endpoint === "holds"
        ? msg("holdPending", "確認メールを送りました。メール内のボタンを押すと取置きが成立します（リンクの有効期限は 2 時間）。成立するまでは、ほかの方も購入できます。")
        : msg("restockPending", "確認メールを送りました。メール内のボタンを押すと登録が完了します。");
      finishForm(form);
      setStatus(form, done);
      announce(done);
      // 2xx の後に 1 回だけ（受付の文言を出した後）。メールアドレスは載せない。
      push(endpoint === "holds" ? "request_hold" : "subscribe_restock", {
        item_id: itemIdFor(root),
        hold_context: ctx,
      });
      return;
    }

    resetTurnstile(form);
    const text = failureMessage(endpoint, res.status, data);
    if (res.status === 403 && data && data.code === "captcha_failed") {
      showFieldError(form, "captcha", text);
    } else if (res.status === 422) {
      const serverText = data && data.errors && typeof data.errors.email === "string" && data.errors.email ? data.errors.email : text;
      showFieldError(form, "email", serverText);
    }
    setStatus(form, text);
    announce(text);
  } catch {
    resetTurnstile(form);
    const text = msg("holdError", "送信できませんでした。時間をおいて再度お試しください。");
    setStatus(form, text);
    announce(text);
  } finally {
    pendingForms.delete(form);
    form.classList.remove("is-loading");
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = false;
      submit.removeAttribute("aria-busy");
    }
  }
}

/* ---------------------------------------------------------------- holder 本人の表示 */

/**
 * 同一オリジンの URL だけを通す（REST の値でも外部へ飛ばさない）。
 *
 * @param {unknown} value
 * @returns {string}
 */
function sameOriginUrl(value) {
  if (typeof value !== "string" || value === "") {
    return "";
  }
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? url.href : "";
  } catch {
    return "";
  }
}

function node(tag, className, text) {
  const el = document.createElement(tag);
  if (className) {
    el.className = className;
  }
  if (text !== undefined) {
    el.textContent = text;
  }
  return el;
}

/**
 * holder 向けの表示を描く。
 *
 * @param {HTMLElement} holder `[data-sp-hold-viewer]`
 * @param {{ends_label?: string, page_url?: string}} data
 * @returns {void}
 */
function renderHolder(holder, data) {
  const id = holder.getAttribute("data-sp-id") || "";
  const live = holder.getAttribute("data-sp-variant") === "live";
  const scope = holder.closest(".p-site-cta, .p-live__foot") || holder.parentElement;
  if (scope) {
    scope.querySelectorAll("[data-sp-hold-other]").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.hidden = true;
      }
    });
  }

  const ends = typeof data.ends_label === "string" ? data.ends_label.trim() : "";
  const text = ends
    ? msg("holdViewerYou", "あなたが取置き中です（%sまで）").replace("%s", ends)
    : msg("holdViewerYouNoDate", "あなたが取置き中です");
  const pageUrl = sameOriginUrl(data.page_url);

  holder.textContent = "";
  const info = node(live ? "span" : "p", live ? "p-live__holder-text" : "p-site-cta__notice p-site-cta__notice--holder", text);
  if (pageUrl) {
    info.appendChild(document.createTextNode(" "));
    const link = node("a", live ? "p-live__holder-link" : "p-site-cta__holder-link", msg("holdViewerOpen", "取置きページを開く"));
    link.href = pageUrl;
    info.appendChild(link);
  }
  holder.appendChild(info);

  const button = node("button", "c-button c-button--secondary c-button--buy js-sp-checkout", msg("holdViewerBuy", "このデザインで申し込む"));
  button.type = "button";
  button.setAttribute("data-sp-action", "checkout");
  button.setAttribute("data-sp-id", id);
  if (live) {
    holder.appendChild(button);
  } else {
    const wrap = node("p", "p-site-cta__buy");
    wrap.appendChild(button);
    // 購入前の注記（fix/legal-terms FIX-12）を申込みボタンの直下へ。文言はサーバが <template> に描く。
    const terms = scope ? scope.querySelector("template[data-sp-hold-terms]") : null;
    if (terms instanceof HTMLTemplateElement) {
      wrap.appendChild(terms.content.cloneNode(true));
    }
    holder.appendChild(wrap);
  }
  holder.hidden = false;
  holder.classList.add("is-holder");
}

/**
 * @returns {void}
 */
function initViewers() {
  const holders = Array.from(document.querySelectorAll("[data-sp-hold-viewer]")).filter((el) => el instanceof HTMLElement);
  if (holders.length === 0) {
    return;
  }
  /** @type {Map<string, HTMLElement[]>} */
  const byId = new Map();
  holders.forEach((el) => {
    const id = el.getAttribute("data-sp-id") || "";
    if (!/^[1-9][0-9]*$/.test(id)) {
      return;
    }
    const list = byId.get(id) || [];
    list.push(el);
    byId.set(id, list);
  });

  byId.forEach(async (list, id) => {
    try {
      const res = await fetchWithTimeout(`${restBase()}holds/viewer?product_id=${encodeURIComponent(id)}`, {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      if (data && data.holder === true) {
        list.forEach((el) => renderHolder(el, data));
      }
    } catch {
      /* 表示の上乗せなので、失敗しても他ユーザー向けの表示のまま */
    }
  });
}

/* ---------------------------------------------------------------- init */

/**
 * @returns {void}
 */
export function initHold() {
  const roots = document.querySelectorAll("[data-sp-hold], [data-sp-restock]");
  if (roots.length > 0 && typeof window.fetch === "function") {
    // JS が動くときだけ見せる（展開・送信は JS が要るため。no-JS で壊れた導線を見せない）。
    roots.forEach((root) => {
      if (root instanceof HTMLElement) {
        root.hidden = false;
      }
    });
    document.addEventListener("click", onToggleClick);
    document.addEventListener("submit", onSubmit);
  }
  initViewers();
}
